"use client";

import { useEffect, useState } from "react";
import { useParams, useRouter } from "next/navigation";
import { Box, FormControlLabel, Switch } from "@mui/material";
import { Text } from "@leabankab/component-library";
import { DevPanel } from "@leabankab/component-library/dev-panel";
import {
	COUNTRY_SWITCH_OPTIONS,
	type CountryCode,
} from "@/config/country-config";
import { getApiCountryName } from "@/config/country-metadata";
import { COUNTRY_SETTINGS } from "@/config/country-settings";
import { useApplication } from "@/contexts/application-context";
import { useFormContext } from "@/contexts/form";
import { useRuntimeConfig } from "@/contexts/runtime-config";
import { useStepNavigation } from "@/contexts/step-navigation-context";
import {
	isAutofillEnabled,
	setAutofillEnabled,
} from "@/dev/console/autofill-preference";
import { setDevCountry } from "@/dev/console/set-dev-country";
import { useAppCountry } from "@/hooks/use-app-country";
import { StepType, getStepSequence } from "@/types/routes";
import { mapFormToGoldfingerLoanApplication } from "@/utils/mapping/map-form-to-goldfinger-loan-application";
import { readStepCookie } from "@/utils/read-step-cookie";
import { setStep } from "@/utils/step-cookie";
import {
	getMockPresetForApplication,
	recordMockPresetForApplication,
} from "./utils/mock-preset-storage";
import { postMockServerRequest } from "./utils/mock-server-request";

const STEP_LABELS: Record<StepType, string> = {
	[StepType.Landing]: "Start",
	[StepType.AboutLoan]: "Om lånet",
	[StepType.PersonalInformationContact]: "Personuppgifter",
	[StepType.PersonalInformationCivil]: "Civilinformation",
	[StepType.EconomyEmployment]: "Sysselsättning",
	[StepType.EconomyCars]: "Bilar",
	[StepType.EconomyDebt]: "Skulder",
	[StepType.EconomyResidence]: "Boende",
	[StepType.CoApplicantChoice]: "Medsökare",
	[StepType.CoApplicantContact]: "Medsökare kontakt",
	[StepType.CoApplicantCivil]: "Medsökare civilinfo",
	[StepType.CoApplicantEmployment]: "Medsökare sysselsättning",
	[StepType.CoApplicantCars]: "Medsökare bilar",
	[StepType.CoApplicantDebt]: "Medsökare skulder",
	[StepType.CoApplicantResidence]: "Medsökare boende",
	[StepType.Summary]: "Sammanfattning",
	[StepType.Submitted]: "Skickad",
};

const STATUS_OPTIONS: DevPanel.StatusOption<string>[] = [
	{ value: "pending", label: "Pending" },
	{ value: "approved", label: "Approved" },
	{ value: "documentsent", label: "Document sent" },
	{ value: "declined", label: "Declined" },
	{ value: "canceled", label: "Canceled" },
] as const;

const STATUS_COLOR: Record<string, DevPanel.StatusColor> = {
	pending: "warning",
	approved: "success",
	documentsent: "info",
	declined: "error",
	canceled: "neutral",
};

export const DevFlowControls = () => {
	const router = useRouter();
	const { refreshStep } = useStepNavigation();
	const { reloadApplicationCache } = useApplication();
	const { applicationId } = useParams<{ applicationId?: string }>();

	const [mounted, setMounted] = useState(false);
	const [currentStepByApplicationId, setCurrentStepByApplicationId] =
		useState<Record<string, StepType>>({});
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [activeStatus, setActiveStatus] = useState<string | null>(null);
	// Follows the server-resolved country; a switch lands after `router.refresh()`.
	const activeCountry = useAppCountry();
	const [autofillEnabled, setAutofillEnabledState] = useState(true);
	const { devConsole, localMock: usingMock } = useRuntimeConfig();
	const canSetStatus = usingMock;
	const dataSourceLabel = usingMock ? "MOCK" : "LIVE API";
	const visibleApplicationSteps = getStepSequence(
		COUNTRY_SETTINGS[activeCountry],
		true
	)
		.filter((step) => step !== StepType.Submitted)
		.map((step) => ({ step, label: STEP_LABELS[step] }));

	const preset = applicationId
		? getMockPresetForApplication(applicationId)
		: undefined;
	const currentStep = applicationId
		? (currentStepByApplicationId[applicationId] ?? readStepCookie())
		: readStepCookie();

	useEffect(() => {
		// Intentional: flip to client-only render after mount to avoid the
		// cookie-driven hydration mismatch below.
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setMounted(true);
		setAutofillEnabledState(isAutofillEnabled(devConsole));
	}, [devConsole]);

	const handleJumpToStep = async (step: StepType) => {
		if (!applicationId) return;
		setError(null);
		setBusy(true);
		try {
			setStep(step);
			setCurrentStepByApplicationId((previous) => ({
				...previous,
				[applicationId]: step,
			}));
			if (preset) {
				recordMockPresetForApplication(applicationId, {
					...preset,
					step,
				});
			}
			refreshStep();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	};

	const handleSetStatus = async (applicationStatus: string) => {
		if (!applicationId) return;
		setError(null);
		setActiveStatus(applicationStatus);
		try {
			await postMockServerRequest("/api/mock-server/set-status", {
				uuid: applicationId,
				applicationStatus,
			});
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		}
	};

	const handleReseed = async () => {
		if (!applicationId || !preset) return;
		setError(null);
		setBusy(true);
		try {
			await postMockServerRequest("/api/mock-server/reseed-application", {
				uuid: applicationId,
				presetKey: preset.presetKey,
			});
			await reloadApplicationCache();
			refreshStep();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	};

	const handleSwitchCountry = async (country: CountryCode) => {
		if (country === activeCountry) return;
		setError(null);
		setBusy(true);
		try {
			await setDevCountry(country);
			router.refresh();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	};

	// Autofill only feeds form defaults, which are read at mount: reload.
	const handleToggleAutofill = (enabled: boolean) => {
		setAutofillEnabledState(enabled);
		setAutofillEnabled(enabled);
		window.location.reload();
	};

	const currentVisibleStepIndex = visibleApplicationSteps.findIndex(
		({ step }) => step === currentStep
	);

	// Live form values: watch() (no args) subscribes to every field so the
	// payload preview re-renders on each keystroke.
	const landingPageForm = useFormContext("landingPageForm").watch();
	const aboutForm = useFormContext("aboutForm").watch();
	const personalInformationForm = useFormContext(
		"personalInformationForm"
	).watch();
	const economyForm = useFormContext("economyForm").watch();

	// The backend body the app would send: the same mapping over the same live
	// form values that `useSaveApplication` persists on every step.
	const goldfingerPayload = mapFormToGoldfingerLoanApplication({
		landingPageForm,
		aboutForm,
		personalInformationForm,
		economyForm,
		appId: applicationId,
		country: getApiCountryName(activeCountry),
	});

	// Render client-only: step/country state comes from cookies and would
	// otherwise mismatch the server-rendered markup and trip hydration.
	if (!mounted) {
		return null;
	}

	return (
		<Box
			sx={(theme) => ({
				position: "fixed",
				right: 16,
				bottom: 16,
				zIndex: theme.zIndex.snackbar,
				width: 400,
				maxHeight: "calc(100dvh - 32px)",
				overflowY: "auto",
			})}
		>
			<DevPanel.Root badge={dataSourceLabel} defaultMinimised={false}>
				<DevPanel.Country
					value={activeCountry}
					onChange={handleSwitchCountry}
					options={COUNTRY_SWITCH_OPTIONS.map(
						({ country, label }) => ({
							value: country,
							label,
							disabled: busy,
						})
					)}
				/>
				<DevPanel.Steps
					steps={visibleApplicationSteps.map(({ label }) => ({
						label,
						disabled: busy,
					}))}
					currentStep={currentVisibleStepIndex + 1}
					onStepSelect={(step) =>
						handleJumpToStep(visibleApplicationSteps[step - 1].step)
					}
				/>
				<DevPanel.Section label="Autofill">
					<FormControlLabel
						control={
							<Switch
								checked={autofillEnabled}
								onChange={(_, checked) =>
									handleToggleAutofill(checked)
								}
							/>
						}
						label={autofillEnabled ? "On" : "Off"}
					/>
				</DevPanel.Section>
				{canSetStatus && (
					<DevPanel.Status
						value={activeStatus ?? ""}
						onChange={handleSetStatus}
						options={STATUS_OPTIONS.map((option) => ({
							value: option.value,
							label: option.label,
							color: STATUS_COLOR[option.value],
						}))}
					/>
				)}
				<DevPanel.Payload
					value={goldfingerPayload}
					placement="side"
					badge="live"
					label="Payload preview"
					title="Goldfinger payload"
				/>
				<DevPanel.Footer
					onReseed={usingMock && preset ? handleReseed : undefined}
					onCopyUrl={() => {}}
					url={
						typeof window !== "undefined"
							? window.location.href
							: undefined
					}
					onConsole={() => router.push("/")}
				/>
				{error && (
					<Text
						render={<p />}
						textStyle="body-sm"
						color="status.error.base"
					>
						{error}
					</Text>
				)}
			</DevPanel.Root>
		</Box>
	);
};
